import { useEffect } from 'react';
import { genLogger } from '../utils';

const checkCallbackIsFunc = (fn: Function) => {
  if (typeof fn !== 'function') throw new Error('Callback not a function');
};

const useContactPrivate = (logger: any, callback: Function) => {
  checkCallbackIsFunc(callback);
  useEffect(() => {
    let isCancelled = false;
    const { log } = logger;
    // Don't destructure sub
    const sub = window.connect.contact((c) => {
      log('contacted');
      try {
        if (isCancelled === false) {
          callback(c);
        } else {
          log('was canceled, not calling callback');
        }
      } catch (e) {
        logger.error('connect error', e);
      }
    });
    return () => {
      isCancelled = true;
      log('unsubscribing');
      sub.unsubscribe();
    };
  }, [logger, callback]);
};

export const useContact = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useContact');
  useContactPrivate(logger, callback);
};

export const useConnected = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useConnected');
  logger.log('init');

  useContactPrivate(logger, (c: any) => {
    c.onConnected(() => {
      logger.log('connected');
      try {
        callback(c);
      } catch (e) {
        logger.error('connect error', e);
      }
    });
  });
};

export const useConnecting = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useConnecting');
  logger.log('init');

  useContactPrivate(logger, (c: any) => {
    c.onConnecting(() => {
      logger.log('connecting');
      try {
        callback(c);
      } catch (e) {
        logger.error('connecting error', e);
      }
    });
  });
};

export const useDestroy = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useDestroy');
  useContactPrivate(logger, (c: any) => {
    c.onDestroy(() => {
      logger.log('destroyed');
      try {
        callback(c);
      } catch (e) {
        logger.error('connect error', e);
      }
    });
  });
};

export const useEnd = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useEnd');
  useContactPrivate(logger, (c: any) => {
    c.onDestroy(() => {
      logger.log('end');
      try {
        callback(c);
      } catch (e) {
        logger.error('end error', e);
      }
    });
  });
};

export const useCallCompleted = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useCallCompleted');
  useContactPrivate(logger, (c: any) => {
    let wasOnCall = false;
    c.onConnected(() => {
      logger.log('call initiated');
      wasOnCall = true;
    });
    c.onACW(() => {
      logger.log('ACW initiated');
      if (wasOnCall) {
        logger.log('ACW after on call');
        try {
          callback(c);
        } catch (e) {
          logger.error('connect error', e);
        }
      } else {
        logger.log('ACW without being on call');
      }
    });
  });
};

export const useCallAccepted = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useCallCompleted');
  useContactPrivate(logger, (c: any) => {
    let wasOnCall = false;
    c.onConnected(() => {
      logger.log('call initiated');
      wasOnCall = true;
    });
    c.onAccepted(() => {
      logger.log('Accepted initiated');
      if (wasOnCall) {
        logger.log('Accepted call');
        try {
          callback(c);
        } catch (e) {
          logger.error('connect error', e);
        }
      } else {
        logger.log('Accepted without being on call');
      }
    });
  });
};

// Connect agent
const useAgentPrivate = (logger: any, callback: Function) => {
  checkCallbackIsFunc(callback);
  useEffect(() => {
    let isCancelled = false;
    const { log } = logger;
    // Don't destructure sub
    const sub = window.connect.agent((a) => {
      try {
        if (isCancelled === false) {
          callback(a);
        } else {
          log('was canceled, not calling callback');
        }
      } catch (e) {
        logger.error('connect error', e);
      }
    });
    return () => {
      isCancelled = true;
      log('unsubscribing');
      sub.unsubscribe();
    };
  }, [logger, callback]);
};

export const useAgentOnAfterCallWork = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useAgentOnAfterCallWork');

  useAgentPrivate(logger, (agent: any) => {
    agent.onAfterCallWork((agent: any, acw: any) => {
      logger.log('connecting');
      try {
        callback(agent, acw);
      } catch (e) {
        logger.error('connecting error', e);
      }
    });
  });
};

export const useAgentOnStateChange = (callback: Function) => {
  checkCallbackIsFunc(callback);
  const logger = genLogger('useAgentOnAfterCallWork');

  useAgentPrivate(logger, (agent: any) => {
    agent.onStateChange(({ agent, oldState, newState }: any) => {
      logger.log('connecting');
      try {
        callback(agent, oldState, newState);
      } catch (e) {
        logger.error('connecting error', e);
      }
    });
  });
};
